/**
 * @brisa/ui · admin/empty-state/EmptyState
 *
 * Feature: admin-basico-login (W3.2 v1.2) · UI_SPEC §10 Tema Neutro Admin
 *
 * Estado vazio genérico para superfícies admin — substitui o
 * `EmptyShortcutsState` facelift descartado em v1.2. Visual neutro shadcn:
 * Card-like centrado em `bg-muted/30` com optional icon Lucide + title
 * (font-medium text-2xl) + body (text-muted-foreground).
 *
 * Generic suficiente pra reuso:
 *   - W3.2: estado vazio do dashboard admin ("Suas ferramentas aparecerão aqui")
 *   - W3.3: CRUD vazio ("Nenhum club cadastrado ainda. Crie o primeiro.")
 *   - W3.x: qualquer empty state operacional
 *
 * Caller passa copy localizada via props (não tem COPY inline como o
 * EmptyShortcutsState facelift tinha — admin neutro privilegia reuso sobre
 * conveniência i18n por feature).
 *
 * Sem 'use client' — apresentacional puro.
 */
import * as React from 'react';

import { cn } from '@brisa/ui/lib/utils';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Título do estado vazio (h2 font-medium text-2xl). */
  title: string;
  /** Texto explicativo abaixo do título (text-muted-foreground). */
  body: string;
  /**
   * Ícone Lucide opcional renderizado acima do título (h-10 w-10
   * text-muted-foreground). Passe `<Wrench />`, `<Inbox />`, etc.
   */
  icon?: React.ReactNode;
  /**
   * Slot opcional pra CTA (Button shadcn). Renderizado abaixo do body.
   * No W3.2 admin dashboard fica vazio (AC-08: estado vazio explicativo,
   * não conversacional). Em W3.3 CRUD pode ter "Criar primeiro club".
   */
  action?: React.ReactNode;
}

export function EmptyState({
  title,
  body,
  icon,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <section
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center gap-4 rounded-lg border border-dashed border-border bg-muted/30 px-8 py-16 text-center',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span
          data-slot="empty-state-icon"
          className="inline-flex h-10 w-10 items-center justify-center text-muted-foreground"
        >
          {icon}
        </span>
      ) : null}
      <h2
        data-slot="empty-state-title"
        className="font-body text-2xl font-medium leading-tight text-foreground"
      >
        {title}
      </h2>
      <p
        data-slot="empty-state-body"
        className="max-w-[42ch] font-body text-sm leading-relaxed text-muted-foreground"
      >
        {body}
      </p>
      {action ? <div data-slot="empty-state-action" className="mt-2">{action}</div> : null}
    </section>
  );
}
